import React, { useEffect } from 'react';
import {
  IonApp,
  IonRouterOutlet,
  isPlatform,
  setupIonicReact,
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { Redirect, Route } from 'react-router-dom';
import { addIcons } from 'ionicons';
import {
  chevronBack,
  chevronForward,
  arrowBack,
  arrowBackOutline,
  arrowBackSharp,
} from 'ionicons/icons';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';
import './index.css';

import { About, ContactPage } from '@/modules/about';
import { TopicRouter } from '@/modules/topic';
import LegRedirect from '@/modules/share/LegRedirect';
import NotFoundRedirect from '@/components/NotFoundRedirect';
import { Help } from '@/modules/help';
import { IDVConfirmation } from '@/modules/idv';
import { UserSettingsRouter, UserSettingsProvider } from '@/modules/settings';
import ProfileWizardRouter from '@/modules/profile/ProfileWizardRouter';
import AddressBookRouter from '@/modules/contact/AddressBookRouter';
import { AppConfigProvider } from '@/modules/config';
import { ContactPickerProvider } from '@/contexts/ContactPickerContext';
import { SuppressibleNoticesProvider } from '@/contexts/SuppressibleNoticesContext';
import { UserProvider } from '@/modules/user/UserContext';
import { ContactStateProvider } from '@/modules/contact';
import { TopicStateProvider } from '@/modules/topic/TopicStateContext';
import TokenExpirationMonitor from '@/modules/user/TokenExpirationMonitor';
import SnowballSyncMonitor from '@/hooks/SnowballSyncMonitor';
import AddressBookSyncMonitor from '@/modules/contact/AddressBookSyncMonitor';
import DuplicatesPrompt from '@/modules/contact/DuplicatesPrompt';
import LaunchQueueHandler from '@/components/LaunchQueueHandler';
import SendingOverlay from '@/components/SendingOverlay';
import ProfileAttributionShareConstraint from '@/components/ProfileAttributionShareConstraint';
import { ActionGuardConstraintsProvider } from '@/hooks/actionGuardConstraints';
import { suppressAccessoryBar } from '@/modules/footer';
import { OutboxModal, OutboxProvider } from '@/modules/outbox';

setupIonicReact({
  mode: isPlatform('android') ? 'md' : 'ios',
  // On desktop there's no software keyboard, so Ionic's input scroll-assist
  // (which lifts a focused field above where the keyboard would be) only jitters
  // the content up a couple pixels on focus. Disable it there; keep it on touch
  // platforms (mobile web + native wrapper), where it does real keyboard
  // avoidance alongside our own FooterDock/useKeyboardPin handling.
  scrollAssist: !isPlatform('desktop'),
});

// Pre-register icons used by Ionic built-ins (e.g. IonBackButton) so
// Ionicons never falls back to URL construction, which fails when
// Stencil's resource base URL hasn't been initialized yet.
addIcons({
  chevronBack,
  chevronForward,
  arrowBack,
  arrowBackOutline,
  arrowBackSharp,
});

const App: React.FC = () => {
  // Suppress the iOS keyboard input-accessory bar app-wide. Effective only in
  // the native Capacitor wrapper; on Safari web the bar shows (no working
  // suppression exists — see accessoryBar.ts) and the footer dock measures the
  // keyboard region live so nothing breaks.
  useEffect(() => suppressAccessoryBar(), []);

  return (
    <AppConfigProvider>
      <ContactPickerProvider>
        <UserSettingsProvider>
          <SuppressibleNoticesProvider>
            <ContactStateProvider>
              <TopicStateProvider>
                <IonApp>
                  <IonReactRouter>
                    <UserProvider>
                      <ActionGuardConstraintsProvider>
                        <OutboxProvider>
                          <ProfileAttributionShareConstraint />
                          <LaunchQueueHandler />
                          <TokenExpirationMonitor />
                          <SnowballSyncMonitor />
                          <AddressBookSyncMonitor />
                          <DuplicatesPrompt />
                          <SendingOverlay />
                          <IonRouterOutlet>
                            {/* The app opens on the topic list. A redirect (not a
                                second route rendering Topics) keeps one canonical
                                URL for the list, so its page lives in a single
                                place in TopicRouter's view stack. */}
                            <Route exact path="/">
                              <Redirect to="/topics" />
                            </Route>
                            <Route
                              exact
                              path="/b/:legId"
                              component={LegRedirect}
                            />
                            <Route path="/topics" component={TopicRouter} />
                            <Route exact path="/help" component={Help} />
                            <Route exact path="/about" component={About} />
                            <Route
                              exact
                              path="/contact"
                              component={ContactPage}
                            />
                            <Route
                              exact
                              path="/idv/:id"
                              component={IDVConfirmation}
                            />
                            <Route
                              path="/settings"
                              component={UserSettingsRouter}
                            />
                            <Route
                              path="/profile"
                              component={ProfileWizardRouter}
                            />
                            <Route path="/ab" component={AddressBookRouter} />
                            {/* No path: IonRouterOutlet's not-found route. */}
                            <Route component={NotFoundRedirect} />
                          </IonRouterOutlet>
                          <OutboxModal />
                        </OutboxProvider>
                      </ActionGuardConstraintsProvider>
                    </UserProvider>
                  </IonReactRouter>
                </IonApp>
              </TopicStateProvider>
            </ContactStateProvider>
          </SuppressibleNoticesProvider>
        </UserSettingsProvider>
      </ContactPickerProvider>
    </AppConfigProvider>
  );
};

export default App;
